import React, { useState, useEffect } from 'react';
import { FaUserShield, FaPlus, FaEdit, FaTrash, FaEye, FaKey } from 'react-icons/fa';
import { adminAPI } from '../../services/api';
import { toast } from 'react-toastify';

const AdminRoles = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getRoles();
      if (response.success) {
        setRoles(response.roles);
        setError(null);
      } else {
        setError(response.message);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
      let errorMessage = 'Failed to load roles';

      if (error.status === 401) {
        errorMessage = 'Unauthorized - Please login again';
      } else if (error.status === 403) {
        errorMessage = 'Access denied - Admin role required';
      } else if (error.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRole = async (roleData) => {
    try {
      const response = await adminAPI.createRole(roleData);
      if (response.success) {
        toast.success('Role created successfully');
        setShowCreateModal(false);
        fetchRoles();
      }
    } catch (error) {
      console.error('Error creating role:', error);
      toast.error(error.message || 'Failed to create role');
    }
  };

  const handleUpdateRole = async (roleData) => {
    try {
      const response = await adminAPI.updateRole(selectedRole.id, roleData);
      if (response.success) {
        toast.success('Role updated successfully');
        setShowEditModal(false);
        fetchRoles();
      }
    } catch (error) {
      console.error('Error updating role:', error);
      toast.error(error.message || 'Failed to update role');
    }
  };

  const handleDeleteRole = async (roleId) => {
    if (!window.confirm('Are you sure you want to delete this role? This action cannot be undone.')) return;

    try {
      const response = await adminAPI.deleteRole(roleId);
      if (response.success) {
        toast.success('Role deleted successfully');
        fetchRoles();
      }
    } catch (error) {
      console.error('Error deleting role:', error);
      toast.error(error.message || 'Failed to delete role');
    }
  };

  const RoleModal = ({ role, onClose, onSave, isEdit = false }) => {
    const [formData, setFormData] = useState({
      name: role?.name || '',
      display_name: role?.display_name || '',
      description: role?.description || '',
      permissions: role?.permissions || {},
      display_order: role?.display_order || 0
    });

    const availablePermissions = [
      { key: 'can_login', label: 'Can Login' },
      { key: 'can_match', label: 'Can Match' },
      { key: 'can_message', label: 'Can Message' },
      { key: 'can_boost', label: 'Can Boost Profile' },
      { key: 'can_see_likes', label: 'Can See Likes' },
      { key: 'can_moderate', label: 'Can Moderate Content' },
      { key: 'can_review_reports', label: 'Can Review Reports' },
      { key: 'can_manage_users', label: 'Can Manage Users' },
      { key: 'can_manage_ads', label: 'Can Manage Ads' },
      { key: 'can_manage_finance', label: 'Can Manage Finance' },
      { key: 'can_system_settings', label: 'Can Access System Settings' }
    ];

    const handlePermissionChange = (permissionKey, checked) => {
      setFormData(prev => ({
        ...prev,
        permissions: {
          ...prev.permissions,
          [permissionKey]: checked
        }
      }));
    };

    const handleSubmit = (e) => {
      e.preventDefault();
      onSave(formData);
    };

    return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-4 sm:p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">
                {isEdit ? 'Edit Role' : 'Create New Role'}
              </h2>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                <FaPlus className="w-5 h-5 transform rotate-45" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Role Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value.toLowerCase() })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="e.g., moderator"
                    required
                    disabled={isEdit && ['admin', 'user', 'premium', 'moderator'].includes(role.name)}
                  />
                  <p className="text-xs text-gray-500 mt-1">Lowercase, no spaces</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Display Name</label>
                  <input
                    type="text"
                    value={formData.display_name}
                    onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="e.g., Moderator"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  rows={3}
                  placeholder="Describe what this role can do..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Permissions</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {availablePermissions.map((permission) => (
                    <div key={permission.key} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.permissions[permission.key] || false}
                        onChange={(e) => handlePermissionChange(permission.key, e.target.checked)}
                        className="h-4 w-4 text-pink-600 border-gray-300 rounded focus:ring-pink-500"
                      />
                      <label className="ml-2 text-sm text-gray-700">
                        {permission.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Display Order</label>
                <input
                  type="number"
                  value={formData.display_order}
                  onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  min="0"
                />
                <p className="text-xs text-gray-500 mt-1">Lower numbers appear first</p>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-pink-600 text-white py-2 px-4 rounded-md hover:bg-pink-700"
                >
                  {isEdit ? 'Update Role' : 'Create Role'}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  };

  const getPermissionCount = (permissions) => {
    if (!permissions) return 0;
    return Object.values(permissions).filter(Boolean).length;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error</h3>
            <div className="mt-2 text-sm text-red-700">{error}</div>
            <div className="mt-4">
              <button
                onClick={fetchRoles}
                className="bg-red-100 hover:bg-red-200 text-red-800 px-3 py-2 rounded-md text-sm font-medium"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 lg:space-y-8 xl:space-y-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold text-gray-900">Role Management</h1>
          <p className="text-gray-600 text-sm sm:text-base">Create and manage user roles and permissions</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-pink-600 text-white px-3 sm:px-4 py-2 rounded-md hover:bg-pink-700 flex items-center self-start sm:self-center"
        >
          <FaPlus className="w-4 h-4 mr-2" />
          Create Role
        </button>
      </div>

      {/* Roles Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {roles.map((role) => (
          <div key={role.id} className="bg-white rounded-lg shadow-sm border p-4 sm:p-6 lg:p-8 xl:p-10">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <div className="flex items-center min-w-0 flex-1">
                <FaUserShield className={`w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3 flex-shrink-0 ${
                  role.name === 'admin' ? 'text-red-500' :
                  role.name === 'moderator' ? 'text-blue-500' :
                  role.name === 'premium' ? 'text-yellow-500' : 'text-green-500'
                }`} />
                <div className="min-w-0 flex-1">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">{role.display_name}</h3>
                  <p className="text-xs sm:text-sm text-gray-500 truncate">@{role.name}</p>
                </div>
              </div>
              {['admin', 'user', 'premium', 'moderator'].includes(role.name) && (
                <span className="inline-flex px-1.5 sm:px-2 py-0.5 sm:py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800 ml-2 flex-shrink-0">
                  System
                </span>
              )}
            </div>

            {role.description && (
              <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4 line-clamp-2">{role.description}</p>
            )}

            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <div className="flex items-center text-xs sm:text-sm text-gray-500">
                <FaKey className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                {getPermissionCount(role.permissions)} permissions
              </div>
              <span className="text-xs sm:text-sm text-gray-500">
                Order: {role.display_order}
              </span>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 sm:gap-2">
              <button
                onClick={() => {
                  setSelectedRole(role);
                  setShowEditModal(true);
                }}
                className="flex-1 bg-blue-600 text-white py-2 px-3 rounded-md hover:bg-blue-700 text-xs sm:text-sm font-medium flex items-center justify-center transition-colors"
                disabled={['admin', 'user', 'premium', 'moderator'].includes(role.name)}
                title={['admin', 'user', 'premium', 'moderator'].includes(role.name) ? 'System roles cannot be edited' : 'Edit role'}
              >
                <FaEdit className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                <span className="hidden sm:inline">Edit</span>
              </button>
              <button
                onClick={() => handleDeleteRole(role.id)}
                className="flex-1 bg-red-600 text-white py-2 px-3 rounded-md hover:bg-red-700 text-xs sm:text-sm font-medium flex items-center justify-center transition-colors"
                disabled={['admin', 'user', 'premium', 'moderator'].includes(role.name)}
                title={['admin', 'user', 'premium', 'moderator'].includes(role.name) ? 'System roles cannot be deleted' : 'Delete role'}
              >
                <FaTrash className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                <span className="hidden sm:inline">Delete</span>
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <RoleModal
          onClose={() => setShowCreateModal(false)}
          onSave={handleCreateRole}
        />
      )}

      {/* Edit Modal */}
      {showEditModal && selectedRole && (
        <RoleModal
          role={selectedRole}
          onClose={() => setShowEditModal(false)}
          onSave={handleUpdateRole}
          isEdit={true}
        />
      )}
    </div>
  );
};

export default AdminRoles;